import api from '../api';
import { io } from 'socket.io-client';
import { getBackendUrl } from '../utils/apiUrl';
import { browserLogger } from '../utils/browserLogger';
import { invalidateMatchCache } from './matchService';

// Get base API URL from environment or fallback
const baseUrl = getBackendUrl();
console.log('Using backend URL for WebSocket:', baseUrl);

// Store socket instance globally
let socket = null;
let messageHandlers = [];
let messageReadHandlers = []; // Array to track read message handlers

// Channel-specific handlers (by conversationId)
let channelMessageHandlers = {}; // Map of conversationId -> array of handlers
let channelReadHandlers = {}; // Map of conversationId -> array of handlers
// Typing handlers — separate map so we don't fire message handlers on typing
// pings. Backend emits both `remoteTyping` (authed peer typing in the
// conversation room) and `guestTyping` (unauthed FNI guest typing) into the
// same conversation room, so we route both into one local channel.
let channelTypingHandlers = {}; // Map of conversationId -> array of handlers
// Negotiation state-change handlers. Backend emits `negotiationStateChanged`
// when the swipe transitions (interview requested, accepted, declined, etc).
// Subscribers typically respond by refetching match details so derived UI
// (banners, sidebar CTAs, page title) reflects the new state without a
// manual page refresh.
let channelStateHandlers = {}; // Map of conversationId -> array of handlers

let isConnected = false;
let connectionAttemptInProgress = false;
let connectionAttemptTimeout = null;
let reconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_DELAY = 2000; // 2 seconds

/**
 * Service for handling messaging between providers and groups
 * Now includes WebSocket support for real-time messaging
 */
export const messageService = {
  /**
   * Fetch all conversations for the current user
   * @returns {Promise} Promise with list of conversations
   */
  getConversations: async () => {
    try {
      const response = await api.get('/messages/conversations');
      return response.data || [];
    } catch (error) {
      console.error('Error fetching conversations:', error);
      throw error;
    }
  },

  /**
   * Fetch messages for a specific conversation
   * @param {string} conversationId - conversation identifier (can be a userId or swipeId)
   * @returns {Promise} Promise with list of messages
   */
  getMessages: async (conversationId) => {
    try {
      console.log(`Fetching messages for conversation ID: ${conversationId}`);
      const response = await api.get(`/messages/conversations/${conversationId}`);
      console.log('Messages API response:', response);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching messages:', error);
      console.error('Error response:', error.response);
      throw error;
    }
  },

  /**
   * Send a new message in a conversation
   * @param {string} conversationId - conversation identifier
   * @param {string} content - message text
   * @param {object} metadata - optional metadata (e.g., attachment info)
   * @returns {Promise} Promise with created message
   */
  sendMessage: async (
    conversationId,
    content,
    metadata = null,
    options = {},
  ) => {
    try {
      const payload = { content };
      if (metadata) {
        payload.metadata = metadata;
      }
      if (options?.clientActionId) {
        payload.clientActionId = options.clientActionId;
      }
      const response = await api.post(`/messages/conversations/${conversationId}`, payload);
      return response.data;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  },

  /**
   * Fetch messages between two users for a specific match
   * @param {string} matchId - match/swipe identifier
   * @param {string} groupUserId - group user ID
   * @param {string} providerUserId - provider user ID
   * @returns {Promise} Promise with list of messages
   */
  getMessagesByUsers: async (matchId, groupUserId, providerUserId) => {
    try {
      console.log(`Fetching messages for match ID: ${matchId} between users ${groupUserId} and ${providerUserId}`);
      // First try fetching by match ID
      const response = await api.get(`/messages/conversations/${matchId}`);
      console.log('Messages API response by matchId:', response);
      
      // If no messages found or error, try querying by user IDs
      if (!response.data || response.data.length === 0) {
        // Note: This would require a backend endpoint that accepts two user IDs
        // Since we don't have such an endpoint yet, we'll stick with the matchId approach
        console.log('No messages found by matchId, could try user IDs in future');
      }
      
      return response.data || [];
    } catch (error) {
      console.error('Error fetching messages between users:', error);
      console.error('Error response:', error.response);
      throw error;
    }
  },

  /**
   * Connect to websocket server for real-time message updates
   * @param {string} token - JWT auth token
   * @returns {Promise} Promise that resolves when socket is connected
   */
  connectSocket: async (token) => {
    // If already connected, return the existing socket
    if (socket && isConnected) {
      console.log('Socket already connected, reusing existing connection');
      return socket;
    }
    
    // If connection attempt is already in progress, return the pending connection promise
    if (connectionAttemptInProgress) {
      console.log('Connection attempt already in progress, waiting for it to complete');
      return new Promise((resolve, reject) => {
        const checkInterval = setInterval(() => {
          if (socket && isConnected) {
            clearInterval(checkInterval);
            resolve(socket);
          } else if (!connectionAttemptInProgress) {
            clearInterval(checkInterval);
            reject(new Error('Connection attempt failed'));
          }
        }, 100);
        
        // Set a timeout for this waiting process
        setTimeout(() => {
          clearInterval(checkInterval);
          reject(new Error('Timed out waiting for existing connection attempt'));
        }, 5000);
      });
    }
    
    // Clear any existing connection attempt timeout
    if (connectionAttemptTimeout) {
      clearTimeout(connectionAttemptTimeout);
      connectionAttemptTimeout = null;
    }
    
    // If socket exists but is disconnected, clean it up
    if (socket) {
      console.log('Cleaning up disconnected socket');
      socket.disconnect();
      socket = null;
    }
    
    // Mark that we're starting a connection attempt
    connectionAttemptInProgress = true;

    try {
      console.log('Connecting to message websocket at:', `${baseUrl}/ws/messages`);
      
      // Create new socket connection
      socket = io(`${baseUrl}/ws/messages`, {
        auth: { token },
        transports: ['websocket'],
        forceNew: true,  // Force a new connection to ensure clean state
        reconnectionAttempts: MAX_RECONNECT_ATTEMPTS,
        reconnection: true,
        reconnectionDelay: RECONNECT_DELAY,
        timeout: 15000 // Increased timeout for more reliable connections
      });

      return new Promise((resolve, reject) => {
        let hasResolved = false;

        // Set a timeout for the initial connection
        const connectionTimeout = setTimeout(() => {
          if (!hasResolved) {
            connectionAttemptInProgress = false;
            console.error('Connection attempt timed out after 10 seconds');
            reject(new Error('Connection attempt timed out'));
          }
        }, 10000);

        // Setup event handlers
        socket.on('connect', () => {
          console.log('Message websocket connected, socket ID:', socket.id);
          browserLogger.info('socket.connect', { id: socket.id });
          isConnected = true;
          connectionAttemptInProgress = false;
          reconnectAttempts = 0; // Reset reconnect attempts on successful connection
          clearTimeout(connectionTimeout);

          // Mark as resolved and resolve the promise
          hasResolved = true;
          resolve(socket);
        });

        socket.on('connect_error', (error) => {
          console.error('Message websocket connection error:', error);
          browserLogger.error('socket.connect_error', { message: error?.message });
          reconnectAttempts++;
          console.log(`Connection attempt ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS} failed`);

          // Only reject if we've exceeded max attempts
          if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
            connectionAttemptInProgress = false;
            clearTimeout(connectionTimeout);
            if (!hasResolved) {
              hasResolved = true;
              reject(error);
            }
          }
        });

        // Socket.io built-in reconnect attempt event (manager-level).
        socket.on('reconnect_attempt', (n) => {
          browserLogger.info('socket.reconnect_attempt', { attempt: n });
        });

        socket.on('disconnect', (reason) => {
          console.log('Message websocket disconnected:', reason);
          browserLogger.warn('socket.disconnect', { reason });
          isConnected = false;
          
          // If we haven't resolved the promise yet, don't reject on server disconnect
          // This allows the connect event to still trigger and resolve if reconnection happens
          if (!hasResolved && reason !== 'io server disconnect') {
            clearTimeout(connectionTimeout);
            hasResolved = true;
            reject(new Error(`Socket disconnected: ${reason}`));
          }
          
          // Handle reconnection
          if (reason === 'io server disconnect' || reason === 'transport close') {
            console.log('Disconnect reason requires a manual reconnection');
            // Schedule reconnect to avoid immediate reconnect loops
            setTimeout(() => {
              if (!isConnected && !connectionAttemptInProgress) {
                console.log('Attempting to reconnect after disconnect...');
                messageService.connectSocket(token).catch(err => {
                  console.error('Reconnect attempt failed:', err);
                });
              }
            }, RECONNECT_DELAY);
          }
        });

        // Listen for new messages
        socket.on('newMessage', (payload) => {
          console.log('New message received via websocket:', payload);
          browserLogger.info('socket.event_received', {
            event: 'newMessage',
            conversationId:
              payload?.conversationId ||
              payload?.swipeId ||
              payload?.message?.conversationId,
            payload,
          });

          // Extract conversation ID from various possible sources
          // First check root level, then inside message object if it exists
          const message = payload.message || payload;

          const conversationId = payload.conversationId ||
                               payload.swipeId ||
                               (payload.conversation && payload.conversation._id) ||
                               payload.conversation ||  // Sometimes conversation is just the ID string
                               message.conversationId ||
                               message.swipeId ||
                               (message.conversation && message.conversation._id) ||
                               message.conversation;
                               
          console.log('Extracted conversationId:', conversationId, 'from payload:', payload);
          
          // First notify channel-specific handlers if we can identify the conversation
          if (conversationId && channelMessageHandlers[conversationId]) {
            console.log(`Notifying ${channelMessageHandlers[conversationId].length} channel-specific handlers for conversation ${conversationId}`);
            channelMessageHandlers[conversationId].forEach(handler => {
              try {
                handler(payload);
              } catch (error) {
                console.error('Error in channel-specific message handler:', error);
              }
            });
          } else {
            console.log('No channel-specific handlers found for message. ConversationId:', conversationId);
          }
          
          // Then notify global handlers (for backwards compatibility)
          messageHandlers.forEach(handler => {
            try {
              handler(payload);
            } catch (error) {
              console.error('Error in global message handler:', error);
            }
          });
        });
        
        // Listen for message read events
        socket.on('messageRead', (payload) => {
          console.log('Message read event received via websocket:', payload);
          browserLogger.info('socket.event_received', {
            event: 'messageRead',
            conversationId:
              payload?.conversationId ||
              payload?.swipeId ||
              payload?.message?.conversationId,
            payload,
          });

          // Extract conversation ID from various possible sources
          const conversationId = payload.conversationId ||
                                payload.swipeId ||
                                payload.message?.conversationId ||
                                payload.message?.swipeId ||
                                (payload.message?.conversation && payload.message.conversation._id) ||
                                (payload.conversation && payload.conversation._id) ||
                                payload.conversation;
          
          // First notify channel-specific handlers if we can identify the conversation
          if (conversationId && channelReadHandlers[conversationId]) {
            console.log(`Notifying ${channelReadHandlers[conversationId].length} channel-specific read handlers for conversation ${conversationId}`);
            channelReadHandlers[conversationId].forEach(handler => {
              try {
                handler(payload);
              } catch (error) {
                console.error('Error in channel-specific read handler:', error);
              }
            });
          } else {
            console.log('No channel-specific read handlers found for message. ConversationId:', conversationId);
          }
          
          // Then notify global handlers (for backwards compatibility)
          messageReadHandlers.forEach(handler => {
            try {
              handler(payload);
            } catch (error) {
              console.error('Error in message read handler:', error);
            }
          });
        });
        
        // Listen for message unsent events
        socket.on('messageUnsent', (payload) => {
          console.log('Message unsent event received via websocket:', payload);
          browserLogger.info('socket.event_received', {
            event: 'messageUnsent',
            conversationId: payload?.conversationId,
            messageId: payload?.messageId,
            payload,
          });

          // Extract conversation ID and message ID
          const conversationId = payload.conversationId;
          const messageId = payload.messageId;
          
          // Notify channel-specific handlers
          if (conversationId && channelMessageHandlers[conversationId]) {
            console.log(`Notifying ${channelMessageHandlers[conversationId].length} channel-specific handlers about unsent message ${messageId}`);
            channelMessageHandlers[conversationId].forEach(handler => {
              try {
                handler({ ...payload, type: 'unsent' });
              } catch (error) {
                console.error('Error in channel-specific unsend handler:', error);
              }
            });
          }
          
          // Also notify global handlers
          messageHandlers.forEach(handler => {
            try {
              handler({ ...payload, type: 'unsent' });
            } catch (error) {
              console.error('Error in global unsend handler:', error);
            }
          });
        });
        
        // Listen for bulk read events
        socket.on('bulkRead', (payload) => {
          console.log('Bulk read event via websocket:', payload);
          browserLogger.info('socket.event_received', {
            event: 'bulkRead',
            conversationId:
              payload?.conversationId ||
              payload?.swipeId ||
              payload?.message?.conversationId,
            payload,
          });

          // Extract conversation ID similarly to messageRead event
          const conversationId = payload.conversationId ||
                                  payload.swipeId ||
                                  payload.message?.conversationId ||
                                  payload.message?.swipeId ||
                                  (payload.message?.conversation && payload.message.conversation._id) ||
                                  (payload.conversation && payload.conversation._id) ||
                                  payload.conversation;

          // First notify channel-specific handlers if we can identify the conversation
          if (conversationId && channelReadHandlers[conversationId]) {
            console.log(`Notifying ${channelReadHandlers[conversationId].length} channel-specific bulk read handlers for conversation ${conversationId}`);
            channelReadHandlers[conversationId].forEach(handler => {
              try {
                handler({
                  ...payload,
                  isBulkRead: true,
                  conversationId,
                });
              } catch (error) {
                console.error('Error in channel-specific bulk read handler:', error);
              }
            });
          } else {
            console.log('No channel-specific bulk read handlers found. ConversationId:', conversationId);
          }

          // Then notify global handlers for backwards compatibility
          messageReadHandlers.forEach(handler => {
            if (handler) {
              try {
                handler({
                  ...payload,
                  isBulkRead: true,
                  conversationId,
                });
              } catch (error) {
                console.error('Error in bulk read handler:', error);
              }
            }
          });
        });

        // Listen for typing pings on the authed conversation room.
        // - `remoteTyping` is broadcast when a logged-in peer is typing
        //   (carries `from` = peer userId so we can ignore our own echoes).
        // - `guestTyping` is broadcast when an FNI guest (unauthed recruiter)
        //   is typing in the same conversation.
        // Both surface the same UI affordance; we route them into one set of
        // channel-keyed handlers.
        const dispatchTyping = (eventName) => (payload) => {
          const conversationId =
            payload?.conversationId ||
            payload?.swipeId ||
            payload?.room?.replace?.(/^conversation:/, '') ||
            null;
          if (!conversationId) return;
          const handlers = channelTypingHandlers[conversationId];
          if (!handlers || handlers.length === 0) return;
          handlers.forEach((handler) => {
            try {
              handler({
                conversationId,
                typing: !!payload?.typing,
                from: payload?.from || null,
                source: eventName === 'guestTyping' ? 'guest' : 'remote',
              });
            } catch (error) {
              console.error(`Error in ${eventName} handler:`, error);
            }
          });
        };
        socket.on('remoteTyping', dispatchTyping('remoteTyping'));
        socket.on('guestTyping', dispatchTyping('guestTyping'));

        // Listen for negotiation state transitions (interview requested,
        // accepted, declined, credentialing, hired, etc). Backend fans the
        // broadcast out to every paired-swipe room (conversation:<id> for
        // each swipeId in the pair) so the event lands no matter which
        // room the client joined.
        //
        // The payload carries:
        //  - `swipeId`        — the actor's swipe (whichever side acted)
        //  - `conversationId` — the canonical provider-initiated swipe _id
        //  - `swipeIds`       — every swipeId in the conversation pair
        //                       (provider + group swipe[s]); always at
        //                       least one element. Use these as lookup
        //                       keys so a handler registered under either
        //                       side's URL `matchId` is invoked. Without
        //                       this, when the provider initiates a
        //                       transition both swipeId and conversationId
        //                       collapse to providerSwipeId, missing any
        //                       company-side handler keyed under
        //                       groupSwipeId.
        socket.on('negotiationStateChanged', (payload) => {
          const lookupKeys = new Set();
          if (payload?.conversationId) lookupKeys.add(payload.conversationId);
          if (payload?.swipeId) lookupKeys.add(payload.swipeId);
          if (Array.isArray(payload?.swipeIds)) {
            for (const id of payload.swipeIds) {
              if (id) lookupKeys.add(id);
            }
          }
          if (lookupKeys.size === 0) return;

          // Bust the matchService SWR cache for this swipe BEFORE dispatching
          // handlers. Subscribers (e.g. `useNegotiationStateSync`) react by
          // calling `matchService.getMatch(matchId)` which has a 60s "fresh"
          // window — without invalidation the refetch returns the
          // pre-transition match, leaving banners and the status badge
          // stuck on the old state until the user manually refreshes.
          for (const key of lookupKeys) invalidateMatchCache(key);

          const dispatched = new Set();
          for (const key of lookupKeys) {
            const handlers = channelStateHandlers[key];
            if (!handlers || handlers.length === 0) continue;
            for (const handler of handlers) {
              if (dispatched.has(handler)) continue;
              dispatched.add(handler);
              try {
                handler(payload);
              } catch (error) {
                console.error(
                  'Error in negotiationStateChanged handler:',
                  error,
                );
              }
            }
          }
        });

        // Set connection timeout
        connectionAttemptTimeout = setTimeout(() => {
          if (!isConnected) {
            console.error('Message websocket connection timeout');
            connectionAttemptInProgress = false;
            reject(new Error('Connection timeout'));
            
            // Clean up the socket
            if (socket) {
              socket.disconnect();
              socket = null;
            }
          }
        }, 15000);
      });
    } catch (error) {
      console.error('Failed to initialize message websocket:', error);
      connectionAttemptInProgress = false;
      throw error;
    }
  },

  /**
   * Disconnect from websocket server
   */
  disconnectSocket: () => {
    if (socket) {
      console.log('Disconnecting message websocket');
      socket.disconnect();
      socket = null;
      isConnected = false;
      // Also clear all message handlers when explicitly disconnecting
      messageHandlers = [];
      messageReadHandlers = [];
      console.log('Cleared all message handlers');
    }
  },

  /**
   * Register handler for new messages
   * @param {function} handler - Function to call when new message received
   * @returns {function} Function to unregister handler
   */
  onNewMessage: (handler) => {
    if (typeof handler !== 'function') {
      console.error('Handler must be a function');
      return () => {};
    }

    messageHandlers.push(handler);
    console.log(`New message handler registered. Total handlers: ${messageHandlers.length}`);

    // Return function to unregister handler
    return () => {
      messageHandlers = messageHandlers.filter(h => h !== handler);
      console.log(`Message handler unregistered. Total handlers: ${messageHandlers.length}`);
    };
  },
  
  /**
   * Register handler for message read events
   * @param {function} handler - Function to call when a message is marked as read
   * @returns {function} Function to unregister handler
   */
  onMessageRead: (handler) => {
    if (typeof handler !== 'function') {
      console.error('Handler must be a function');
      return () => {};
    }
    
    console.log('Registering message read handler');
    messageReadHandlers.push(handler);
    
    // Return function to remove this handler
    return () => {
      const index = messageReadHandlers.indexOf(handler);
      if (index !== -1) {
        messageReadHandlers.splice(index, 1);
        console.log('Unregistered message read handler');
      }
    };
  },

  /**
   * Get the socket instance for direct operations like subscribe/unsubscribe
   * @returns {Object|null} Socket.io instance or null if not connected
   */
  getSocket: () => {
    // Return the socket only if it's connected
    if (socket && isConnected) {
      return socket;
    }
    return null;
  },
  
  /**
   * Check if the socket is currently connected
   * @returns {boolean} True if socket is connected, false otherwise
   */
  isSocketConnected: () => {
    return isConnected && socket !== null;
  },
  
  /**
   * Subscribe to messages for a specific conversation
   * @param {string} conversationId - The conversation ID to subscribe to
   * @param {function} messageHandler - Handler for new messages in this conversation
   * @param {function} readHandler - Handler for read events in this conversation
   * @returns {function} Function to unsubscribe from this conversation
   */
  subscribeToConversation: (
    conversationId,
    messageHandler,
    readHandler,
    typingHandler,
    stateChangeHandler,
  ) => {
    console.log(`Subscribing to conversation: ${conversationId}`);

    if (!conversationId) {
      console.error('Cannot subscribe to conversation without a valid ID');
      return () => {}; // Return no-op unsubscribe function
    }

    // Initialize handler arrays if needed
    if (!channelMessageHandlers[conversationId]) {
      channelMessageHandlers[conversationId] = [];
    }
    if (!channelReadHandlers[conversationId]) {
      channelReadHandlers[conversationId] = [];
    }
    if (!channelTypingHandlers[conversationId]) {
      channelTypingHandlers[conversationId] = [];
    }
    if (!channelStateHandlers[conversationId]) {
      channelStateHandlers[conversationId] = [];
    }

    // Add handlers for this conversation
    if (messageHandler) {
      channelMessageHandlers[conversationId].push(messageHandler);
    }

    if (readHandler) {
      channelReadHandlers[conversationId].push(readHandler);
    }

    if (typingHandler) {
      channelTypingHandlers[conversationId].push(typingHandler);
    }

    if (stateChangeHandler) {
      channelStateHandlers[conversationId].push(stateChangeHandler);
    }

    // Get the current count of handlers for logging
    const messageHandlerCount = channelMessageHandlers[conversationId].length;
    const readHandlerCount = channelReadHandlers[conversationId].length;

    console.log(`Added handlers for conversation ${conversationId}. Now has ${messageHandlerCount} message handlers and ${readHandlerCount} read handlers`);
    
    // Join the conversation room on the server for channel-based updates
    const socketInstance = messageService.getSocket();
    if (socketInstance) {
      // Emit joinRoom event to server with conversationId
      socketInstance.emit('joinRoom', conversationId);
      browserLogger.info('socket.event_emitted', { event: 'joinRoom', conversationId });
      console.log(`Joined conversation room: ${conversationId}`);
    } else {
      console.warn(`Cannot join room ${conversationId} - socket not connected`);
    }
    
    // Return unsubscribe function
    return () => {
      console.log(`Unsubscribing from conversation: ${conversationId}`);

      // Remove message handler if provided
      if (messageHandler && channelMessageHandlers[conversationId]) {
        channelMessageHandlers[conversationId] = channelMessageHandlers[conversationId].filter(h => h !== messageHandler);
      }

      // Remove read handler if provided
      if (readHandler && channelReadHandlers[conversationId]) {
        channelReadHandlers[conversationId] = channelReadHandlers[conversationId].filter(h => h !== readHandler);
      }

      if (typingHandler && channelTypingHandlers[conversationId]) {
        channelTypingHandlers[conversationId] = channelTypingHandlers[conversationId].filter(h => h !== typingHandler);
      }

      if (stateChangeHandler && channelStateHandlers[conversationId]) {
        channelStateHandlers[conversationId] = channelStateHandlers[conversationId].filter(h => h !== stateChangeHandler);
      }

      // Clean up empty handler arrays
      if (channelMessageHandlers[conversationId]?.length === 0) {
        delete channelMessageHandlers[conversationId];
      }

      if (channelReadHandlers[conversationId]?.length === 0) {
        delete channelReadHandlers[conversationId];
      }

      if (channelTypingHandlers[conversationId]?.length === 0) {
        delete channelTypingHandlers[conversationId];
      }

      if (channelStateHandlers[conversationId]?.length === 0) {
        delete channelStateHandlers[conversationId];
      }
      
      // Leave the conversation room on the server
      const socketInstance = messageService.getSocket();
      if (socketInstance) {
        // Emit leaveRoom event to server with conversationId
        socketInstance.emit('leaveRoom', conversationId);
        browserLogger.info('socket.event_emitted', { event: 'leaveRoom', conversationId });
        console.log(`Left conversation room: ${conversationId}`);
      }
      
      console.log(`Unsubscribed from conversation: ${conversationId}`);
    };
  },
  
  /**
   * Mark a message as read
   * @param {string} messageId - ID of message to mark as read
   * @param {string} recipientType - Type of recipient ('group' or 'provider')
   * @returns {Promise}
   */
  markMessageAsRead: async (messageId, recipientType) => {
    try {
      console.log(`Marking message ${messageId} as read, recipientType: ${recipientType}`);
      const response = await api.post(`/messages/${messageId}/read`, { recipientType });
      
      // After successful API call, emit the read event via websocket if connected
      if (isConnected && socket) {
        console.log('Emitting messageRead event via websocket');
        socket.emit('messageRead', { messageId, recipientType });
        browserLogger.info('socket.event_emitted', {
          event: 'messageRead',
          messageId,
          recipientType,
        });
      }

      return response.data;
    } catch (error) {
      console.error('Error marking message as read:', error);
      throw error;
    }
  },
  
  /**
   * Mark a message and all prior messages as read
   * @param {string} messageId - ID of the target message (and all prior messages) to mark as read
   * @param {string} conversationId - ID of the conversation
   * @param {string} recipientType - Type of recipient ('group' or 'provider')
   * @returns {Promise}
   */
  markMessageAndPriorAsRead: async (messageId, conversationId, recipientType) => {
    try {
      console.log(`Marking message ${messageId} and all prior messages as read, conversation: ${conversationId}`);
      const response = await api.post(`/messages/${messageId}/read-all-prior`, { 
        conversationId,
        recipientType 
      });
      
      // After successful API call, emit the bulkRead event via websocket if connected
      if (isConnected && socket) {
        console.log('Emitting bulkRead event via websocket');
        socket.emit('bulkRead', {
          messageId,
          conversationId,
          recipientType,
          timestamp: new Date().toISOString()
        });
        browserLogger.info('socket.event_emitted', {
          event: 'bulkRead',
          messageId,
          conversationId,
          recipientType,
        });
      }
      
      return response.data;
    } catch (error) {
      console.error('Error marking message and prior as read:', error);
      throw error;
    }
  },
  
  /**
   * Upload file attachment to a conversation
   * @param {string} conversationId - conversation/swipe ID
   * @param {File} file - file to upload
   * @param {string} content - optional message content
   * @param {function} onProgress - optional progress callback
   * @returns {Promise} Promise with upload result
   */
  uploadFile: async (conversationId, file, content = null, onProgress = null, metadata = null) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (content) {
        formData.append('content', content);
      }
      if (metadata) {
        formData.append('metadata', JSON.stringify(metadata));
      }
      
      const response = await api.post(`/messages/conversations/${conversationId}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percentCompleted);
          }
        },
      });
      
      return response.data;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  },
  
  /**
   * Get file download URL
   * @param {string} fileId - file ID
   * @returns {string} Download URL
   */
  getFileDownloadUrl: (fileId) => {
    return `${baseUrl}/messages/files/${fileId}`;
  },
  
  /**
   * Get file blob for preview
   * @param {string} fileId - file ID
   * @returns {Promise} Promise with file blob
   */
  getFileBlob: async (fileId) => {
    try {
      const response = await api.get(`/messages/files/${fileId}`, {
        responseType: 'blob',
      });
      return response;
    } catch (error) {
      console.error('Error getting file blob:', error);
      throw error;
    }
  },
  
  /**
   * Download file with authentication
   * @param {string} fileId - file ID
   * @param {string} filename - original filename
   * @returns {Promise} Promise that triggers download
   */
  downloadFile: async (fileId, filename) => {
    try {
      const response = await api.get(`/messages/files/${fileId}`, {
        responseType: 'blob',
      });
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return { success: true };
    } catch (error) {
      console.error('Error downloading file:', error);
      throw error;
    }
  },
  
  /**
   * Delete file attachment
   * @param {string} fileId - file ID to delete
   * @returns {Promise} Promise with delete result
   */
  deleteFile: async (fileId) => {
    try {
      const response = await api.post(`/messages/files/${fileId}/delete`);
      return response.data;
    } catch (error) {
      console.error('Error deleting file:', error);
      throw error;
    }
  },
  
  /**
   * FNI-12: emit typing / stopped-typing over the existing ws/messages
   * socket. The backend MessageGateway debounces and re-broadcasts to the
   * guest namespace so guests see "Provider is typing…" on their page.
   *
   * @param {string} conversationId - swipe/conversation ID
   * @param {boolean} typing - true while composing, false on send/blur
   */
  emitTyping: (conversationId, typing) => {
    if (!isConnected || !socket) return;
    if (!conversationId) return;
    socket.emit('typing', { conversationId, typing: !!typing });
    browserLogger.info('socket.event_emitted', {
      event: 'typing',
      conversationId,
      typing: !!typing,
    });
  },

  /**
   * Unsend a message (mark it as unsent)
   * @param {string} messageId - ID of the message to unsend
   * @returns {Promise} Promise with unsend result
   */
  unsendMessage: async (messageId) => {
    try {
      console.log(`Unsending message ${messageId}`);
      const response = await api.post(`/messages/${messageId}/unsend`);
      
      // After successful API call, emit the unsend event via websocket if connected
      if (isConnected && socket) {
        console.log('Emitting messageUnsent event via websocket');
        socket.emit('messageUnsent', { messageId });
        browserLogger.info('socket.event_emitted', {
          event: 'messageUnsent',
          messageId,
        });
      }
      
      return response.data;
    } catch (error) {
      console.error('Error unsending message:', error);
      throw error;
    }
  },
};

/**
 * Standalone register/unregister helpers for `negotiationStateChanged` socket
 * events. These exist alongside the legacy inline `stateChangeHandler` param
 * on subscribeToConversation — both write into the same module-scope
 * `channelStateHandlers` map, so a hook-based consumer (see
 * `useNegotiationStateSync`) can register without rerunning the broader
 * subscribeToConversation lifecycle (joinRoom/leaveRoom etc) which is owned
 * by the chat surface.
 */
export function registerNegotiationStateChangedHandler(swipeId, handler) {
  if (!swipeId || typeof handler !== 'function') return;
  if (!channelStateHandlers[swipeId]) channelStateHandlers[swipeId] = [];
  channelStateHandlers[swipeId].push(handler);
}

export function unregisterNegotiationStateChangedHandler(swipeId, handler) {
  if (!swipeId || !channelStateHandlers[swipeId]) return;
  channelStateHandlers[swipeId] = channelStateHandlers[swipeId].filter(
    (h) => h !== handler,
  );
  if (channelStateHandlers[swipeId].length === 0) {
    delete channelStateHandlers[swipeId];
  }
}

export default messageService;
